<app-banner-card
  data-testid="campaign-details-banner"
  [breadcrumbs]="(!creatorBriefingMode() || creatorMode()) && !exportMode() ? breadcrumbs() : null"
>
  <app-content-container>
    <!--
      Mobile briefing: the two wrappers below become `contents`, so the logo, name, meta rows and creator pill
      all wrap inside this one row: logo + name on the first line, everything else full width under it.
    -->
    <div
      class="flex flex-1 h-full w-full items-center mt-4 gap-4"
      [ngClass]="{
        'max-md:flex-wrap max-md:items-start max-md:mt-0 max-md:gap-x-3 max-md:gap-y-2': creatorBriefingMode(),
      }"
    >
      @let logoSize = mobileBriefing() ? 56 : 132;

      @if (!campaign()) {
        <ngx-skeleton-loader
          class="shrink-0"
          [style.height.px]="logoSize"
          [theme]="{ width: logoSize + 'px', height: logoSize + 'px', 'border-radius': '8px' }"
        />
      } @else {
        <app-icon-badge
          class="shrink-0"
          [content]="campaign()?.iconId"
          [fallbackText]="campaignExport()?.name ?? campaign()?.name"
          [size]="logoSize"
          [iconSize]="logoSize"
          [fontSize]="mobileBriefing() ? 28 : 64"
          [borderRadius]="mobileBriefing() ? 12 : 8"
        />
      }

      <div class="flex justify-between gap-2 w-full" [ngClass]="{ 'max-md:contents': creatorBriefingMode() }">
        <div
          class="flex flex-col justify-between gap-3 h-[132px]"
          [ngClass]="{ 'max-md:contents': creatorBriefingMode() }"
        >
          <div
            class="flex items-center gap-3 pt-0.5"
            [ngClass]="{
              'max-md:flex-1 max-md:min-w-0 max-md:flex-col max-md:items-start max-md:gap-1.5 max-md:pt-0 max-md:mb-2':
                creatorBriefingMode(),
            }"
          >
            @if (!campaign()) {
              <ngx-skeleton-loader
                class="h-6 max-w-full"
                [theme]="{ width: '256px', 'max-width': '100%', height: '24px', 'border-radius': '4px' }"
              />
              <ngx-skeleton-loader class="h-6" [theme]="{ width: '48px', height: '24px', 'border-radius': '100vw' }" />
            } @else {
              <h2
                class="font-normal line-clamp-1 text-ellipsis max-w-[500px]"
                data-testid="campaign-details-banner-name"
                [ngClass]="{
                  'max-md:line-clamp-2 max-md:max-w-none max-md:text-xl max-md:leading-6 max-md:font-medium':
                    creatorBriefingMode(),
                }"
                matTooltip
                matTooltipPosition="above"
                [appTextClampTooltip]="campaignExport()?.name ?? campaign()?.name ?? ''"
              >
                {{ campaignExport()?.name ?? campaign()?.name }}
              </h2>

              <app-status-badge [status]="campaign()?.status!" />
            }
          </div>

          <div class="flex items-center gap-1" [ngClass]="{ 'max-md:basis-full max-md:gap-2': creatorBriefingMode() }">
            @if (!campaign()) {
              <ngx-skeleton-loader class="h-5" [theme]="{ width: '20px', height: '20px', 'border-radius': '4px' }" />
              <ngx-skeleton-loader class="h-5" [theme]="{ width: '72px', height: '20px', 'border-radius': '4px' }" />
              <span class="text-sm">-</span>
              <ngx-skeleton-loader class="h-5" [theme]="{ width: '72px', height: '20px', 'border-radius': '4px' }" />
            } @else {
              <app-icon src="/icons/calendar-week.svg" alt="Calendar" class="w-4 h-4" />
              <span class="text-sm">{{ campaign()?.start | date: 'dd.MM.yyyy' }}</span>
              <span class="text-sm">-</span>
              <span class="text-sm">{{ campaign()?.end | date: 'dd.MM.yyyy' }}</span>
            }
          </div>

          <div class="flex items-center gap-6" [ngClass]="{ 'max-md:basis-full': creatorBriefingMode() }">
            @if (store.createdByUser$() !== null) {
              <div class="flex items-center gap-2">
                @if (store.createdByUser$() === undefined) {
                  <ngx-skeleton-loader
                    class="h-[15px] flex"
                    [theme]="{ width: '27px', height: '15px', 'border-radius': '4px' }"
                  />
                  <div class="flex items-center gap-1">
                    <ngx-skeleton-loader
                      class="h-5 flex"
                      [theme]="{ width: '20px', height: '20px', 'border-radius': '100vw' }"
                    />
                    <ngx-skeleton-loader
                      class="h-5 flex"
                      [theme]="{ width: '100px', height: '20px', 'border-radius': '4px' }"
                    />
                  </div>
                } @else if (store.createdByUser$() !== null) {
                  <span class="text-[10px] text-shikenso-purple-base-40">
                    {{ 'pages.campaignDetails.banner.owner' | translate }}
                  </span>
                  <div class="flex items-center gap-1">
                    <app-icon-badge
                      [content]="store.createdByUser$()?.iconId"
                      [fallbackText]="store.createdByUser$()?.name"
                      [size]="20"
                      [iconSize]="20"
                      [fontSize]="10"
                      [borderRadius]="100"
                    />
                    <span class="text-sm">{{ store.createdByUser$()?.name }}</span>
                  </div>
                }
              </div>
            }

            <div class="flex items-center gap-2">
              @if (!campaign()) {
                <ngx-skeleton-loader
                  class="h-[15px] flex"
                  [theme]="{ width: '27px', height: '15px', 'border-radius': '4px' }"
                />
                <div class="flex items-center gap-1">
                  <ngx-skeleton-loader
                    class="h-5 flex"
                    [theme]="{ width: '20px', height: '20px', 'border-radius': '100vw' }"
                  />
                  <ngx-skeleton-loader
                    class="h-5 flex"
                    [theme]="{ width: '100px', height: '20px', 'border-radius': '4px' }"
                  />
                </div>
              } @else if (campaign()?.customer) {
                <span
                  class="text-[10px] text-shikenso-purple-base-40"
                  [ngClass]="{ 'max-md:text-xs': creatorBriefingMode() }"
                >
                  {{ 'pages.campaignDetails.banner.client' | translate }}
                </span>

                <div class="flex items-center gap-1">
                  <app-icon-badge
                    [content]="campaign()?.customer?.iconId"
                    [fallbackText]="campaign()?.customer?.name"
                    [size]="20"
                    [iconSize]="20"
                    [fontSize]="10"
                    [borderRadius]="100"
                  />
                  <div class="flex items-center gap-1 cursor-pointer" (click)="onCustomerClick()">
                    <span class="text-sm">{{ campaign()?.customer?.name }}</span>

                    @if (!creatorBriefingMode() && !exportMode()) {
                      <app-icon src="/icons/circle-arrow-up-right.svg" alt="Next" [size]="20" />
                    }
                  </div>
                </div>
              }
            </div>
          </div>

          <div
            class="flex items-center gap-6 pb-0.5"
            [ngClass]="{ 'max-md:basis-full max-md:pb-0': creatorBriefingMode() }"
          >
            <div class="flex items-center gap-3 text-xs" [ngClass]="{ 'max-md:text-sm': creatorBriefingMode() }">
              <div class="flex items-center gap-1">
                @if (!campaignAnalytics()) {
                  <ngx-skeleton-loader
                    class="h-4"
                    [theme]="{ width: '16px', height: '16px', 'border-radius': '4px' }"
                  />
                  <ngx-skeleton-loader
                    class="h-4"
                    [theme]="{ width: '70px', height: '16px', 'border-radius': '4px' }"
                  />
                } @else {
                  <app-icon src="/icons/library.svg" alt="Platform Features" [size]="16" />
                  <span>
                    {{ platformFeatures().length }} {{ 'pages.campaignDetails.export.platforms' | translate }}
                  </span>
                }
              </div>

              @if (!creatorBriefingMode()) {
                <div class="flex items-center gap-1">
                  @if (!campaignAnalytics()) {
                    <ngx-skeleton-loader
                      class="h-4"
                      [theme]="{ width: '16px', height: '16px', 'border-radius': '4px' }"
                    />
                    <ngx-skeleton-loader
                      class="h-4"
                      [theme]="{ width: '65px', height: '16px', 'border-radius': '4px' }"
                    />
                  } @else {
                    <app-icon src="/icons/creators.svg" alt="Creators" [size]="16" />
                    <span> {{ creators().length }} {{ 'pages.campaignDetails.export.creators' | translate }} </span>
                  }
                </div>
              }
            </div>

            @if (!creatorBriefingMode()) {
              <div class="flex items-center gap-1 text-xs">
                @if (!campaign()) {
                  <ngx-skeleton-loader
                    class="h-4"
                    [theme]="{ width: '16px', height: '16px', 'border-radius': '4px' }"
                  />
                  <ngx-skeleton-loader
                    class="h-4"
                    [theme]="{ width: '100px', height: '16px', 'border-radius': '4px' }"
                  />
                } @else if (campaign()?.budget !== null && campaign()?.budget !== undefined && campaign()?.currency) {
                  <div
                    class="flex items-center gap-1"
                    matTooltipPosition="above"
                    [matTooltip]="'pages.campaignForm.sections.basics.budgetLabel' | translate"
                  >
                    <app-icon src="/icons/cash.svg" alt="Budget" [size]="16" />
                    <span> {{ campaign()?.budget! | numberSuffix }} {{ campaign()?.currency! }} </span>
                  </div>
                }
              </div>
            }
          </div>
        </div>

        @if (creatorBriefingMode()) {
          <!-- Mobile: creator and payout merge into one full-width pill. -->
          <div
            class="flex flex-col items-end justify-center gap-2 h-[132px] max-md:basis-full max-md:h-auto max-md:mt-2 max-md:flex-row max-md:items-center max-md:justify-between max-md:gap-3 max-md:rounded-full max-md:border max-md:border-shikenso-purple-base-12 max-md:py-2.5 max-md:pl-2.5 max-md:pr-4"
            data-testid="campaign-details-banner-creator"
          >
            @let avatarSize = mobileBriefing() ? 36 : 50;

            @if (campaign()?.campaignCreators?.[0]) {
              @let campaignCreator = campaign()!.campaignCreators![0];

              <div
                class="flex items-center justify-center gap-3 w-full max-md:w-auto max-md:min-w-0 max-md:justify-start max-md:gap-2.5"
              >
                <app-icon-badge
                  class="shrink-0"
                  [content]="campaignCreator.creator.icon"
                  [fallbackText]="campaignCreator.creator.name"
                  [size]="avatarSize"
                  [iconSize]="avatarSize"
                  [fontSize]="mobileBriefing() ? 18 : 24"
                  [borderRadius]="100"
                />

                <span
                  matTooltip
                  class="font-bold max-w-52 text-ellipsis line-clamp-1 max-md:max-w-none max-md:text-[15px]"
                  matTooltipPosition="above"
                  [appTextClampTooltip]="campaignCreator.creator.name"
                >
                  {{ campaignCreator.creator.name }}
                </span>
              </div>

              @if (campaignCreator.cost !== null && campaignCreator.cost !== undefined && campaign()?.currency) {
                <div
                  class="flex justify-between px-4 py-2 gap-4 w-full rounded-full border border-shikenso-purple-base-12 max-md:w-auto max-md:shrink-0 max-md:items-center max-md:gap-2 max-md:p-0 max-md:border-0"
                >
                  <div class="flex items-center gap-2 text-xs">
                    <app-icon src="/icons/money.svg" alt="Payout" [size]="16" />

                    <span>
                      {{ 'pages.campaignDetails.banner.payout' | translate }}
                    </span>
                  </div>

                  <span class="font-bold max-md:text-base">{{ campaignCreatorCost() }} {{ campaign()?.currency }}</span>
                </div>
              }
            } @else {
              <div class="flex items-center gap-3 max-md:min-w-0 max-md:gap-2.5">
                <ngx-skeleton-loader
                  class="shrink-0"
                  [style.height.px]="avatarSize"
                  [theme]="{ width: avatarSize + 'px', height: avatarSize + 'px', 'border-radius': '100vw' }"
                />
                <ngx-skeleton-loader
                  class="h-6 max-w-full"
                  [theme]="{ width: '208px', 'max-width': '100%', height: '24px', 'border-radius': '4px' }"
                />
              </div>

              <div class="w-full max-md:hidden">
                <ngx-skeleton-loader
                  class="h-[42px] w-full"
                  [theme]="{ width: '100%', height: '42px', 'border-radius': '100vw' }"
                />
              </div>
            }
          </div>
        } @else if (!exportMode()) {
          <div class="flex items-start gap-4">
            @if (!campaign()) {
              <ngx-skeleton-loader
                class="h-8 mr-9"
                [theme]="{ width: '98px', height: '32px', 'border-radius': '100vw' }"
              />
              <ngx-skeleton-loader class="h-8" [theme]="{ width: '32px', height: '32px', 'border-radius': '100vw' }" />
              <ngx-skeleton-loader class="h-8" [theme]="{ width: '32px', height: '32px', 'border-radius': '100vw' }" />
            } @else {
              <app-campaign-export-button
                class="mr-9"
                [analyticsNodes]="analyticsNodes()"
                [campaignAnalytics]="campaignAnalytics()"
              />
              <button
                mat-icon-button
                class="mat-big-icon-button"
                *hasPermission="[EPermissions.campaignUpdateOwn]"
                (click)="onEditClick()"
              >
                <app-icon src="/icons/edit.svg" alt="Edit" />
              </button>
              <button
                mat-icon-button
                floatUi
                type="button"
                class="mat-big-icon-button"
                aria-label="More actions"
                [applyClass]="'max-sm:-translate-x-6 -translate-x-4'"
                [floatUi]="notesContent"
                [matMenuTriggerFor]="campaignBannerActionsMenu"
                [placement]="'BOTTOMEND' | enum: 'FloatUiPlacements'"
                [positionFixed]="true"
                [showTrigger]="floatUiTriggerNone"
                #notesMenuFloatAnchor
              >
                <app-icon src="/icons/dots.svg" alt="More actions" />
              </button>
              <mat-menu #campaignBannerActionsMenu="matMenu">
                <button mat-menu-item type="button" (click)="onNotesClick()">
                  <div class="flex items-center gap-2">
                    <app-icon src="/icons/notes.svg" alt="" [size]="16" />
                    <span>{{ 'pages.campaignForm.sections.notes.title' | translate }}</span>
                  </div>
                </button>

                <button
                  mat-menu-item
                  type="button"
                  *hasPermission="[EPermissions.campaignUpdateOwn]"
                  (click)="onCopyClick()"
                >
                  <div class="flex items-center gap-2">
                    <app-icon src="/icons/copy.svg" alt="" [size]="16" />
                    <span>{{ 'pages.campaigns.datatable.copyCampaign' | translate }}</span>
                  </div>
                </button>

                @if (reprocessEnabled()) {
                  <button
                    mat-menu-item
                    *hasPermission="[EPermissions.campaignUpdateAll]"
                    type="button"
                    [disabled]="store.reprocessLoading$()"
                    (click)="onReprocessClick()"
                  >
                    <div class="flex items-center gap-2">
                      @if (store.reprocessLoading$()) {
                        <app-dots-spinner [absoluteCentered]="true" />
                      } @else {
                        <app-icon src="/icons/refresh.svg" alt="" [size]="16" />
                        <span>{{ 'pages.campaignDetails.banner.reprocess' | translate }}</span>
                      }
                    </div>
                  </button>
                }

                <button
                  mat-menu-item
                  type="button"
                  *hasPermission="[EPermissions.campaignDeleteOwn]"
                  (click)="onDeleteClick()"
                >
                  <div class="flex items-center gap-2">
                    <app-icon src="/icons/trash.svg" alt="" [size]="16" />
                    <span>{{ 'shared.delete' | translate }}</span>
                  </div>
                </button>

                <button
                  mat-menu-item
                  type="button"
                  *hasPermission="null; roles: [ERoles.admin, ERoles.support]"
                  (click)="onTokensClick()"
                >
                  <div class="flex items-center gap-2">
                    <app-icon src="/icons/model-alt.svg" alt="" [size]="16" />
                    <span>{{ 'pages.campaignDetails.banner.tokens' | translate }}</span>
                  </div>
                </button>
              </mat-menu>
            }
          </div>
        }
      </div>
    </div>
  </app-content-container>
</app-banner-card>

<float-ui-content class="z-10 mt-4" #notesContent>
  <div class="flex flex-col gap-2">
    <h3 class="text-md font-bold">
      {{ 'pages.campaignForm.sections.notes.title' | translate }}
    </h3>
    <quill-editor
      class="quill-editor-campaign-notes w-80"
      [ngModel]="campaign()?.notes"
      [placeholder]="'pages.campaignDetails.banner.notesHint' | translate"
      [readOnly]="true"
      [modules]="{ toolbar: false }"
    />
  </div>
</float-ui-content>
